import React, { useContext, useEffect, useRef, useState } from "react";
import "./CategoryOptionsPage.css";
import { UserContext } from "../../../../App";

const toTitleCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const StarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ResetIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const CategoryOptionsPage = ({
  data,
  onCategoryClick,
  selectedCategory,
  backgroundColor = "var(--surface-white)",
  height = "90vh",
  variant = "",
}) => {
  const { color } = useContext(UserContext);
  const [searchData, setSearchData] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);

  const userName = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("retailUserDetails"));
      return u?.name || u?.userName || u?.staffName || null;
    } catch {
      return null;
    }
  })();

  const categories = Array.isArray(data) ? data : [];

  const highlightSearchText = (text, highlight) => {
    if (!highlight || !text) return text;
    const safe = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${safe})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="cop-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const filterData = (value) => {
    if (!value?.length) {
      setFilteredCategories(categories.slice());
      return;
    }
    const filtered = categories.filter((category) =>
      category?.key?.toLowerCase().includes(value?.toLowerCase())
    );
    setFilteredCategories(filtered);
  };

  const handleSearch = (e) => {
    setSearchData(e.target.value);
    filterData(e.target.value);
  };

  useEffect(() => {
    setFilteredCategories(Array.isArray(data) ? data.slice() : []);
  }, [data]);

  const handleCategoryClick = (categoryName) => {
    onCategoryClick(categoryName === selectedCategory ? "" : categoryName);
  };

  const handleReset = () => {
    setSearchData("");
    setFilteredCategories(categories.slice());
    onCategoryClick("");
  };

  const isFav = (c) => ["favorites", "favourites"].includes(c?.key?.toLowerCase());
  const safeFiltered = Array.isArray(filteredCategories) ? filteredCategories : [];
  const favCategory = safeFiltered.find(isFav);
  const regularCategories = safeFiltered.filter((c) => !isFav(c));

  return (
    <div
      className={`cop-shell ${variant ? `cop-${variant}` : ""} ${color === "dark" ? "dark" : ""}`}
      style={{ height }}
    >
      {/* ── Header ── */}
      <div className="cop-header">
        <div className="cop-header-row">
          <span className="cop-title">Categories</span>
          {categories.length > 0 && <span className="cop-count-badge">{categories.length}</span>}
        </div>

        {/* Search */}
        <div className="cop-search-wrapper">
          <span className="cop-search-icon">
            <SearchIcon />
          </span>
          <input
            className="cop-search-input"
            type="text"
            placeholder="Search categories"
            value={searchData}
            onChange={handleSearch}
          />
        </div>

        {/* Drag + Reset row */}
        {/* <div className="cop-drag-row">
          <span className="cop-drag-text">Drag to reorder categories</span>
          <button className="cop-reset-btn" onClick={handleReset}>
            <ResetIcon /> Reset
          </button>
        </div> */}
      </div>

      {/* ── List ── */}
      <div className="cop-list-scroll">
        {categories.length === 0 ? (
          <p className="cop-empty">No Categories Found</p>
        ) : (
          <ul className="cop-list">
            {/* Favorites card */}
            {favCategory && (
              <li
                className={`cop-fav-card ${favCategory.key === selectedCategory ? "active" : ""}`}
                onClick={() => handleCategoryClick(favCategory.key)}
              >
                <span className="cop-fav-star">
                  <StarIcon />
                </span>
                <div className="cop-fav-text">
                  <span className="cop-fav-title">
                    {highlightSearchText(toTitleCase(favCategory.key), searchData)}
                  </span>
                  {userName && <span className="cop-fav-sub">{userName}'s picks</span>}
                </div>
                {favCategory.value != null && (
                  <span className="cop-fav-count">{favCategory.value}</span>
                )}
              </li>
            )}

            {/* Regular categories */}
            {regularCategories.map((cat, idx) => {
              const isActive = cat?.key === selectedCategory;
              const isCombos = cat?.key?.toLowerCase() === "combos";
              return (
                <li
                  key={idx}
                  draggable
                  className={`cop-item ${isCombos ? "combos" : ""} ${isActive ? "active" : ""}`}
                  onClick={() => handleCategoryClick(cat.key)}
                >
                  <span className={`cop-item-name ${isActive ? "active" : ""}`}>
                    {highlightSearchText(toTitleCase(cat.key), searchData)}
                  </span>
                  {cat.value != null && (
                    <span className={`cop-item-count ${isActive ? "active" : ""}`}>
                      {cat.value}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CategoryOptionsPage;
